import {
  ApprovalStatus,
  BranchOperationStatus,
  FaqCategory,
  ReviewStatus,
  UserRole,
} from "../types/admin";

export const roleLabelMap: Record<UserRole, string> = {
  owner: "사업주",
  manager: "점장",
  worker: "직원",
  admin: "관리자",
};

export const approvalStatusLabelMap: Record<string, string> = {
  // 사업주(owner) — API가 내려주는 한글 라벨
  "심사 대기": "심사 대기",
  허용: "허용",
  반려: "반려",
  // 근로자(worker)
  정상: "정상",
  정지: "정지",
  // 레거시/내부 코드 fallback (혹시 영문 값이 올 때)
  pending_role_selection: "심사 대기",
  pending_admin_approval: "심사 대기",
  pending_owner_assignment: "사업주 승인 대기",
  pending_manager_assignment: "점장 승인 대기",
  approved_by_owner: "사업주 승인 완료",
  approved_by_admin: "허용",
  approved: "허용",
  rejected: "반려",
};

/** API 한글 라벨을 우선 표시. 레거시 코드만 매핑 */
export const formatApprovalStatus = (status: string | null | undefined): string => {
  if (!status) {
    return "-";
  }
  return approvalStatusLabelMap[status] || status;
};

/** 목록·뱃지용: 대기 / 허용·승인 / 반려·정지 구분 */
export const approvalStatusTone = (status: string): "pending" | "approved" | "rejected" => {
  if (
    status === "rejected" ||
    status === "반려" ||
    status === "승인 거절" ||
    status === "정지"
  ) {
    return "rejected";
  }
  if (
    status === "허용" ||
    status === "approved" ||
    status === "approved_by_admin" ||
    status === "approved_by_owner" ||
    status === "정상"
  ) {
    return "approved";
  }
  if (status.startsWith("pending") || status === "심사 대기") {
    return "pending";
  }
  return "pending";
};

/** 회원 목록 필터 / 상세 수정용 옵션 */
export const ownerApprovalOptions: Array<{ value: ApprovalStatus; label: string }> = [
  { value: "심사 대기", label: "심사 대기" },
  { value: "허용", label: "허용" },
  { value: "반려", label: "반려" },
];

export const workerApprovalOptions: Array<{ value: ApprovalStatus; label: string }> = [
  { value: "정상", label: "정상" },
  { value: "정지", label: "정지" },
];

export const memberApprovalFilterOptions: Array<{ value: ""; label: string } | { value: ApprovalStatus; label: string }> = [
  { value: "", label: "전체 상태" },
  ...ownerApprovalOptions,
  ...workerApprovalOptions,
];

export const reviewStatusLabelMap: Record<ReviewStatus, string> = {
  pending: "인증 대기",
  approved: "인증 완료",
  rejected: "반려",
};

/** 지점 목록 `status`(운영·심사대기·반려) 뱃지 톤 */
export const branchOperationStatusTone = (
  status: string
): "operating" | "pending" | "rejected" | "neutral" => {
  const normalized = status as BranchOperationStatus;
  if (normalized === "반려") {
    return "rejected";
  }
  if (normalized === "심사대기") {
    return "pending";
  }
  if (normalized === "운영") {
    return "operating";
  }
  return "neutral";
};

export const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatDateDot = (value: string | null | undefined): string => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}.${mm}.${dd}`;
};

export const formatDateTimeDot = (value: string | null | undefined): string => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
};

export const INQUIRY_TYPE_OPTIONS: Array<{ value: FaqCategory; label: string }> = [
  { value: "account", label: "계정/로그인" },
  { value: "contract_payroll", label: "근로계약서·급여명세서" },
  { value: "labor_cost", label: "인건비/점내비용" },
  { value: "staff_management", label: "직원관리(등록·배정·퇴사)" },
  { value: "recruitment", label: "채용" },
  { value: "app_bug", label: "앱 오류/버그" },
  { value: "feature_request", label: "기능 개선 제안" },
  { value: "etc", label: "기타" },
];

export const formatInquiryType = (value: string | null | undefined): string => {
  if (!value) {
    return "-";
  }
  if (value === "service") {
    return "직원관리(등록·배정·퇴사)";
  }
  if (value === "payment") {
    return "인건비/점내비용";
  }
  return INQUIRY_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;
};

export const formatTemplateVersion = (version: string | null | undefined): string => {
  if (!version) return "-";
  if (version.includes("guardian_consent")) return "친권자(후견인) 동의서";
  if (version.includes("minor") || version.includes("youth")) return "연소근로자 표준근로계약서";
  if (version.includes("standard")) return "표준 근로계약서";
  return version;
};
